import React, { useEffect, useState } from 'react';
import { Button, Form } from 'react-bootstrap';
import Select from 'react-select';
import CreatableSelect from 'react-select/creatable';
import { $form as $formSignal, $view as $viewSignal } from 'signals/global.signals';
import Loadable from '../Loadable/Loadable';
import CheckBoxInput from './components/CheckBoxInput';
import InputBoxGroup from './components/InputBoxGroup';
import { formatDate, formatMoney, formatPhone, formatTime, isEmailValid } from './UniversalInput.helpers';
// Add a style tag for cross-browser placeholder styling
const PlaceholderStyles = () => (
  <style>
    {`
      .placeholder-text-light::placeholder {
        color: #ccc !important;
        opacity: 0.6 !important;
      }
      .placeholder-text-light::-webkit-input-placeholder {
        color: #ccc !important;
        opacity: 0.6 !important;
      }
      .placeholder-text-light::-moz-placeholder {
        color: #ccc !important;
        opacity: 0.6 !important;
      }
      .placeholder-text-light:-ms-input-placeholder {
        color: #ccc !important;
        opacity: 0.6 !important;
      }
    `}
  </style>
);

const validateField = (type, value, required, isLoading) => {
  if (isLoading) {
    return '';
  }
  // Handle required for both string and number values
  const isEmpty =
    value === undefined ||
    value === null ||
    (typeof value === 'string' && value.trim() === '') ||
    (typeof value === 'number' && Number.isNaN(value));
  if (required && isEmpty) {
    return 'This field is required';
  }

  switch (type) {
    case 'email':
      return value?.trim() && !isEmailValid(value) ? 'Invalid email format' : '';
    case 'phone':
      return value?.trim() && value.length < 14 ? 'Invalid phone number' : '';
    default:
      return '';
  }
};

const UniversalInput = ({
  type,
  name,
  $form = $formSignal,
  $view = $viewSignal,
  variant = 'form-control', // || form-control-border
  className,
  placeholder,
  inputFormatCallback,
  value,
  customOnChange, // ONLY USE IF NEEDED
  autoComplete,
  isValid,
  isInvalid,
  errorMessage,
  inputBoxGroupOptions = {},
  disabled,
  options = [],
  label,
  labelClassName,
  required,
  helperText,
  inLineLabel,
  ...props
}) => {
  if ((!$form || !name) && !customOnChange) {
    return new Error(`Universal Input has no $form or name (Name: ${name})`);
  }

  const [touched, setTouched] = useState(false);
  const val = $form?.value?.[name];
  const isLoading = $form?.value?.isLoading || $view?.value?.isLoading;
  const validationError = validateField(type, val, required && touched, isLoading);
  const showError = !isLoading && (isInvalid || validationError);
  const errorText = errorMessage || validationError;

  // Local state for money input
  const [localMoney, setLocalMoney] = useState('');

  // Update localMoney when form value changes (for controlled input)
  useEffect(() => {
    if (type === 'money' && val !== undefined) {
      const formatted = val !== undefined && val !== null && val !== '' ? formatMoney(String(val)) : '';
      setLocalMoney(formatted);
    }
  }, [val, type]);

  let component;

  const handleChange = (e) => {
    setTouched(true);
    let newValue = inputFormatCallback ? inputFormatCallback(e.target.value) : e.target.value;

    if (type === 'money') {
      // Remove all non-digit and non-decimal characters
      const numericValue = newValue.replace(/[^\d.]/g, '');
      // Format for display (with $ and commas)
      const formatted = numericValue !== '' ? formatMoney(numericValue) : '';
      setLocalMoney(formatted);

      // Store numeric value in form
      const numberValue = numericValue !== '' ? Number(numericValue) : '';
      if (customOnChange) {
        customOnChange(e, { name, value: numberValue });
      } else {
        $form.update({ ...$form.value, [name]: numberValue });
      }
      return;
    }

    if (type === 'percent') {
      const numericValue = newValue.replace(/[^\d]/g, '');
      newValue = Number(numericValue);
    }

    if (customOnChange) {
      customOnChange(e, { name, value: newValue });
    } else {
      $form.update({ ...$form.value, [name]: newValue });
    }
  };

  const handleBlur = (e) => {
    setTouched(true);
    if (type === 'money') {
      // Convert to string if it's a number, or use empty string if null/undefined
      const moneyStr = String(localMoney || '');
      setLocalMoney(moneyStr ? formatMoney(moneyStr.replace(/[^\d.]/g, '')) : '');
    }
    if (props.onBlur) props.onBlur(e);
  };

  const validation = {
    email: { valid: isEmailValid(val), invalid: !isEmailValid(val) },
    phone: { valid: val?.length === 14, invalid: val?.length < 14 },
    money: { valid: val !== undefined && val !== '', invalid: false },
    percent: { valid: val !== undefined && val !== '', invalid: false },
    text: { valid: val !== undefined && val !== '', invalid: false },
    textarea: { valid: val !== undefined && val !== '', invalid: false },
    number: { valid: val !== undefined && val !== '', invalid: false },
    date: { valid: val !== undefined && val !== '', invalid: false },
    time: { valid: val !== undefined && val !== '', invalid: false },
    password: { valid: val !== undefined && val !== '', invalid: false },
    select: { valid: val !== undefined && val !== '', invalid: false },
  };

  const formatValue = () => {
    if (type === 'phone') {
      return formatPhone(val);
    }
    if (type === 'date' && val instanceof Date) {
      return formatDate(val);
    }
    if (type === 'time' && val instanceof Date) {
      return formatTime(val);
    }
    if (type === 'money') {
      return localMoney;
    }
    if (type === 'percent') {
      const formattedValue = val ? `${val}%` : '';
      return formattedValue;
    }
    return val || value || '';
  };

  if (type === 'select') {
    if (disabled) {
      return (
        <div className={className}>
          <Form.Label htmlFor={`${name}-select`} className="mb-8">
            {label}
            {required && <span className="text-danger ms-1">*</span>}
          </Form.Label>
          <div className="p-8 bg-muted text-gray-300 rounded">{value || 'N/A'}</div>
        </div>
      );
    }
    const handleSelectChange = (selectedOption) => {
      if (customOnChange) {
        customOnChange(selectedOption);
      } else {
        $form.update({
          [name]: selectedOption,
        });
      }
    };
    const selectedValue = value || $form.value[name];
    const selectedOption = Array.isArray(selectedValue) ? selectedValue : options.find(option => option.value === selectedValue?.value);

    component = (
      <>
        <PlaceholderStyles />
        {label && (
          <Form.Label htmlFor={`${name}-select`} className="mb-0">
            {label}
            {required && <span className="text-danger ms-1">*</span>}
          </Form.Label>
        )}
        {props.isCreatable ? (
          <CreatableSelect
            id={`${name}-select`}
            name={name}
            value={selectedOption}
            onChange={handleSelectChange}
            options={options}
            isDisabled={disabled}
            placeholder={placeholder}
            className={`ds-select rounded py-8 ${className} ${showError ? 'is-invalid' : ''}`}
            classNamePrefix="ds-select"
            isClearable={props.isClearable}
            isSearchable
            isMulti={props.isMulti}
            formatCreateLabel={(inputValue) => `+ Create New "${inputValue}"`}
            {...props}
          />
        ) : (
          <Select
            id={`${name}-select`}
            name={name}
            value={selectedOption}
            onChange={handleSelectChange}
            options={options}
            isDisabled={disabled}
            placeholder={placeholder}
            className={`${props.isMulti ? 'ds-multi-select' : 'ds-select'}  rounded py-8 ${className} ${showError ? 'is-invalid' : ''}`}
            classNamePrefix={`${props.isMulti ? 'ds-multi-select' : 'ds-select'}`}
            isMulti={props.isMulti}
            isClearable={props.isClearable}
            isSearchable={props.isSearchable}
            {...props}
          />
        )}
        {showError && <div className="error-feedback text-danger">{errorText}</div>}
      </>
    );
  } else if (type === 'inputBoxGroup') {
    component = (
      <>
        {label && (
          <Form.Label>{label}</Form.Label>
        )}
        <PlaceholderStyles />
        <InputBoxGroup
          name={name}
          $form={$form}
          variant={variant}
          className={className}
          isValid={isValid}
          isInvalid={isInvalid}
          options={inputBoxGroupOptions}
          {...props}
        />
      </>
    );
  } else if (type === 'checkbox') {
    component = (
      // eslint-disable-next-line react/jsx-no-useless-fragment
      <>
        {inLineLabel ? (
          <div className={`d-flex justify-content-between  ${className}`}>
            <Form.Label className={labelClassName}>{label}</Form.Label>
            <div style={{ paddingTop: '3px' }}>
              <CheckBoxInput
                name={name}
                $form={$form}
                className={className}
                {...props}
              />
            </div>
          </div>
        ) : (
          <>
            <Form.Label className={labelClassName}>{label}</Form.Label>
            <CheckBoxInput
              name={name}
              $form={$form}
              className={className}
              {...props}
            />
          </>
        )}
      </>
    );
  } else if (type === 'checkbox-button') {
    const isChecked = props.checked ?? false;
    component = (
      <div className="checkbox-button">
        <Form.Check
          type="checkbox"
          id={name}
          name={name}
          className="d-none"
          checked={isChecked}
          onChange={(e) => {
            if (props.onChange) {
              props.onChange(e);
            } else {
              $form.update({
                ...$form.value,
                [name]: !$form.value?.[name],
              });
            }
          }}
          {...props}
        />
        <label
          htmlFor={name}
          className={`btn ${isChecked ? 'btn-primary' : 'btn-outline-primary'} px-16 py-8`}
        >
          {label}
        </label>
      </div>
    );
  } else if (type === 'file') {
    component = (
      <Form.Group>
        <div style={{ position: 'relative' }}>
          <Button as="label" variant={variant} className={className}>
            {label || 'Choose Files'}
            <Form.Control
              id={`${name}-file-input`}
              type="file"
              multiple
              style={{ display: 'none' }}
              onChange={(e) => {
                const newFiles = e.target.files;
                if (newFiles) {
                  $form.update({
                    attachments: [...($form.value.attachments || []), ...Array.from(newFiles)],
                  });
                }
                customOnChange(e);
              }}
            />
          </Button>
        </div>
      </Form.Group>
    );
  } else {
    component = (
      <>
        {label && (
          <Form.Label htmlFor={`${name}-form-input`} className="mb-8">
            {label}
            {required && <span className="text-danger ms-8">*</span>}
          </Form.Label>
        )}
        {helperText && <div className="text-info small">{helperText}</div>}
        <Form.Control
          as={type === 'textarea' ? 'textarea' : 'input'}
          id={`${name}-form-input`}
          type={type || 'text'}
          value={formatValue()}
          placeholder={placeholder}
          className={`rounded py-8 ps-16 w-100 bg-gray-800 border-gray-700 ${variant} ${className || ''} ${disabled ? 'text-dark-300' : ''} shadow-none py-8 ps-16`}
          name={name}
          autoComplete={autoComplete}
          onChange={handleChange}
          onBlur={handleBlur}
          isValid={validation[type || 'text']?.valid || isValid}
          isInvalid={showError}
          disabled={disabled}
          maxLength={type === 'phone' ? 14 : ''}
          {...props}
        />
        {showError && <div className="error-feedback text-danger small mt-8">{errorText}</div>}
      </>
    );
  }

  return (
    <Loadable signal={$view} template="text">
      {component}
    </Loadable>
  );
};

export default UniversalInput;
